import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2 } from 'lucide-react';
import type { OpsTask, CRMUser } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import { firstOpsTaskEmployeeUid, isOpsTaskEmployeeRole } from '../../utils/userRoles';
import OpsTaskPopoutDetail from './OpsTaskPopoutDetail';
import OpsChecklistSection from './OpsChecklistSection';
import OpsTaskMeetingNotes from './OpsTaskMeetingNotes';
import OpsComments from './OpsComments';
import '../pm/TaskComments.css';
import './OpsCommentsPopout.css';
import './OpsDetailPanel.css';

export interface OpsCommentsPopoutProps {
  task: OpsTask | null;
  teamMembers: CRMUser[];
  highlightCommentId?: string | null;
  onHighlightCommentConsumed?: () => void;
  onUpdate: (id: string, updates: Partial<OpsTask>) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
  onOpenPdf?: (
    url: string,
    title: string,
    documentId: string,
    opsTaskId: string,
    options?: { annotationId?: string; pageNumber?: number },
  ) => void;
  onOpenImage?: (
    url: string,
    title: string,
    documentId: string,
    opsTaskId: string,
    options?: { annotationId?: string; mimeType?: string },
  ) => void;
  onOpenCsv?: (
    url: string,
    title: string,
    documentId: string,
    opsTaskId: string,
    options?: { annotationId?: string },
  ) => void;
}

/** Unified ops task modal: editable task details + comments. */
const OpsCommentsPopout: React.FC<OpsCommentsPopoutProps> = ({
  task,
  teamMembers,
  highlightCommentId,
  onHighlightCommentConsumed,
  onUpdate,
  onDelete,
  onClose,
  onOpenPdf,
  onOpenImage,
  onOpenCsv,
}) => {
  const { user } = useAuth();
  const [commentCount, setCommentCount] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');

  useEffect(() => {
    setConfirmDelete(false);
    setEditingTitle(false);
    setTitleDraft(task?.title || '');
  }, [task?.id, task?.title]);

  useEffect(() => {
    if (!task?.id) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('tc-popout-open');
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('tc-popout-open');
    };
  }, [task?.id, onClose]);

  if (!task?.id) return null;

  const saveTitle = () => {
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== task.title) onUpdate(task.id!, { title: trimmed });
    setEditingTitle(false);
  };

  return createPortal(
    <>
      <div className="tc-popout-backdrop" onClick={onClose} aria-hidden />
      <div
        className="tc-section tc-section--popout tc-section--ops-task"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ops-popout-title"
        onClick={e => e.stopPropagation()}
      >
        <div className="tc-popout-header">
          {editingTitle ? (
            <input
              id="ops-popout-title"
              className="ops-popout-title-input"
              value={titleDraft}
              onChange={e => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={e => {
                if (e.key === 'Enter') saveTitle();
                if (e.key === 'Escape') { setTitleDraft(task.title); setEditingTitle(false); }
              }}
              autoFocus
            />
          ) : (
            <button
              type="button"
              id="ops-popout-title"
              className="tc-popout-title ops-popout-title-btn"
              title={task.title}
              onClick={() => setEditingTitle(true)}
            >
              {task.title}
            </button>
          )}
          <span className="tc-popout-count">{commentCount}</span>
          <div className="ops-popout-header-actions">
            {confirmDelete ? (
              <div className="ops-popout-delete-confirm">
                <span>Delete?</span>
                <button type="button" className="ops-popout-delete-yes" onClick={() => onDelete(task.id!)}>
                  Yes
                </button>
                <button type="button" className="ops-popout-delete-no" onClick={() => setConfirmDelete(false)}>
                  No
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="ops-popout-delete-btn"
                onClick={() => setConfirmDelete(true)}
                title="Delete task"
                aria-label="Delete task"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
          <button
            type="button"
            className="tc-popout-close"
            onClick={onClose}
            title="Close (Esc)"
            aria-label="Close popup"
          >
            <X size={14} />
          </button>
        </div>

        <OpsTaskPopoutDetail task={task} teamMembers={teamMembers} onUpdate={onUpdate} />

        {task.id ? (
          <div className="ops-popout-meeting-notes">
            <OpsTaskMeetingNotes opsTaskId={task.id} variant="popout" />
          </div>
        ) : null}

        <div className="ops-popout-comments-section">
          <div className="ops-popout-checklist-pinned">
            <OpsChecklistSection
              items={task.subtasks || []}
              onChange={(subtasks) => onUpdate(task.id!, { subtasks })}
              teamMembers={teamMembers}
              defaultAssigneeUid={
                firstOpsTaskEmployeeUid(teamMembers, task.assignedTo)
                || (user?.id && isOpsTaskEmployeeRole(
                  teamMembers.find((u) => u.uid === user.id)?.role,
                )
                  ? user.id
                  : undefined)
              }
              variant="stream"
            />
          </div>
          <div className="ops-popout-comments-label">Comments</div>
          <div className="ops-popout-comments-wrap">
            <OpsComments
              key={task.id}
              embedded
              taskId={task.id}
              taskTitle={task.title}
              taskStatus={task.status}
              highlightCommentId={highlightCommentId}
              onHighlightConsumed={onHighlightCommentConsumed}
              onCommentCountChange={setCommentCount}
              onOpenPdf={onOpenPdf ? (url, title, docId, opts) => onOpenPdf(url, title, docId, task.id!, opts) : undefined}
              onOpenImage={onOpenImage ? (url, title, docId, opts) => onOpenImage(url, title, docId, task.id!, opts) : undefined}
              onOpenCsv={onOpenCsv ? (url, title, docId, opts) => onOpenCsv(url, title, docId, task.id!, opts) : undefined}
            />
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default OpsCommentsPopout;
